import type { PortableTextBlock } from '@portabletext/types';
import { graphql, PageProps, useStaticQuery } from 'gatsby';
import { useContext } from 'react';

import { Container } from 'components/GridV2';
import { LocaleContext } from 'components/rebrand/Layout';
import { Link } from 'components/rebrand/Link';

import { FooterLink as TFooterLink, SocialMedia as TSocialMedia } from 'typescriptDefs/layout';

import { trackEvent } from 'helpers';

import { Disclaimers } from './Disclaimers';
import { SocialMedia } from './SocialMedia';

type Props = {
  location: PageProps['location'];
  pageDisclaimer?: PortableTextBlock | PortableTextBlock[];
  pageName: string;
};

const Footer = ({ location, pageDisclaimer, pageName }: Props) => {
  const locale = useContext(LocaleContext);
  const isFr = locale === 'fr';
  const { footer } = useStaticQuery<IFooterGraphQLData>(queryGraphQL);
  const searchParams = location?.search || '';

  return (
    <footer className="bg-primary-500 pb-48 pt-62 text-white lg:pb-58">
      <Container className="px-32 xs:px-0">
        <div className="mb-36 flex w-fit flex-col gap-0 lg:mx-auto lg:flex-row lg:gap-64">
          {footer.links.map((link) => {
            const frLink = link.frUrl ?? `/fr${link.enUrl}`;
            const localizedLink = {
              title: isFr ? link.frTitle : link.enTitle,
              url: (isFr ? frLink : link.enUrl) + searchParams,
              isNew: link.isNew,
            };
            return (
              <Link
                variant="inverse"
                className="py-8 text-md font-medium leading-normal text-white hover:no-underline lg:py-12"
                key={localizedLink.title}
                href={localizedLink.url}
                onClick={() =>
                  trackEvent('footer_link_clicked', {
                    category: pageName,
                    label: localizedLink.title ?? '',
                    locale,
                  })
                }
              >
                {localizedLink.title}
              </Link>
            );
          })}
        </div>
        <SocialMedia
          socialMedia={footer.socialMedia}
          title={{ en: footer.socialMediaTitle, fr: footer.socialMediaTitleFr }[locale]}
        />
        <Disclaimers
          searchParams={searchParams}
          footerDisclaimers={footer.disclaimers}
          pageDisclaimer={pageDisclaimer}
        />
      </Container>
    </footer>
  );
};

export default Footer;

type TextBlock = PortableTextBlock | PortableTextBlock[];
interface IFooterGraphQLData {
  footer: {
    disclaimers:
      | {
          disclaimersColumn: { en: TextBlock; fr: TextBlock }[];
        }[]
      | null;
    links: TFooterLink[];
    socialMedia: TSocialMedia[];
    socialMediaTitle: string;
    socialMediaTitleFr: string;
  };
}

const queryGraphQL = graphql`
  query {
    footer: sanitySiteFooter(name: { eq: "Default" }) {
      disclaimers {
        disclaimersColumn {
          en: _rawEn
          fr: _rawFr
        }
      }
      links {
        enTitle
        frTitle
        enUrl
        frUrl
        isNew
      }
      socialMedia {
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 48)
            }
          }
        }
        url
        urlFr
      }
      socialMediaTitle
      socialMediaTitleFr
    }
  }
`;
